/**
 * Checks one loaded model against its kind in the contract, as the Blender build did before it was
 * written: positions, normals and colours, the triangle budget, its height, and that it stands on
 * its origin without reaching too far. Pure, so the tests run it on the committed art too.
 */
import type * as THREE from 'three';

import { ART_SINK, type ArtKind } from './manifest';

/** Rounding: the .glb holds 32-bit numbers, so a model right at a limit in Blender may be a hair over. */
const SLACK = 0.001;

/** What breaks the contract, in words; empty when the model keeps it. */
export function artProblems(name: string, kind: ArtKind, geometry: THREE.BufferGeometry): string[] {
  const out: string[] = [];
  const position = geometry.getAttribute('position');
  for (const attribute of ['position', 'normal', 'color'])
    if (!(attribute in geometry.attributes)) out.push(`${name}: has no ${attribute}s`);
  if (out.length > 0) return out;
  const triangles = (geometry.index?.count ?? position.count) / 3;
  if (triangles > kind.triangles)
    out.push(`${name}: ${String(triangles)} triangles, the budget is ${String(kind.triangles)}`);
  let top = -Infinity;
  let bottom = Infinity;
  let reach = 0;
  for (let i = 0; i < position.count; i++) {
    const y = position.getY(i);
    top = Math.max(top, y);
    bottom = Math.min(bottom, y);
    reach = Math.max(reach, Math.hypot(position.getX(i), position.getZ(i)));
  }
  const [low, high] = kind.height;
  if (top < low - SLACK || top > high + SLACK)
    out.push(`${name}: ${top.toFixed(2)} tall, it must be ${String(low)} to ${String(high)}`);
  if (bottom < -ART_SINK - SLACK)
    out.push(`${name}: reaches ${(-bottom).toFixed(2)} below the ground`);
  if (reach > kind.footprint + SLACK)
    out.push(`${name}: reaches ${reach.toFixed(2)} sideways, at most ${String(kind.footprint)}`);
  return out;
}
